// src/components/dashboard/misDatos/DatosPaciente.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import HistorialService from "../../../services/HistorialService";
import FilesService from "../../../services/FilesService";
import { AuthContext } from "../../../context/AuthContext";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import moment from "moment";
import "moment/locale/es";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

/* ---------- utilidades ---------- */
const hasValue = (val) => val !== null && val !== undefined && val !== "";

const formatDate = (iso, fallback = "—") =>
  iso ? moment(iso).locale("es").format("DD MMM YYYY, HH:mm") : fallback;

const formatSimpleDate = (iso, fallback = "—") =>
  iso ? moment(iso).locale("es").format("DD MMM YYYY") : fallback;

const formatMetric = (value, unit = "", decimals) => {
  if (!hasValue(value)) {
    return "Sin registro";
  }

  const num = Number(value);
  const display =
    typeof decimals === "number" && !Number.isNaN(num)
      ? Number(num).toFixed(decimals)
      : !Number.isNaN(num)
      ? num.toString()
      : String(value);

  return unit ? `${display} ${unit}`.trim() : display;
};

const formatAgeFromMonths = (value) => {
  const months = Number(value);
  if (!Number.isFinite(months)) return null;
  const totalMonths = Math.max(0, Math.round(months));
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;

  const yearLabel = years > 0 ? `${years} ${years === 1 ? "año" : "años"}` : "";
  const monthLabel =
    remainingMonths > 0
      ? `${remainingMonths} ${remainingMonths === 1 ? "mes" : "meses"}`
      : "";

  if (yearLabel && monthLabel) return `${yearLabel} y ${monthLabel}`;
  if (yearLabel) return yearLabel;
  if (monthLabel) return monthLabel;
  return "0 meses";
};

const deriveAgeInMonths = (patient) => {
  if (!patient) return null;
  const monthsField = Number(patient.edadMeses);
  if (Number.isFinite(monthsField)) {
    return Math.max(0, monthsField);
  }

  const yearsField = Number(patient.edad);
  if (Number.isFinite(yearsField)) {
    return Math.max(0, Math.round(yearsField * 12));
  }

  if (patient.fechaNacimiento) {
    const birth = moment(patient.fechaNacimiento);
    if (birth.isValid()) {
      const diffMonths = moment().diff(birth, "months");
      if (Number.isFinite(diffMonths)) {
        return Math.max(0, diffMonths);
      }
    }
  }

  return null;
};

const activityLabels = {
  escuelaOficina: "Escuela / Oficina",
  trabajoFisico: "Trabajo físico",
  hogar: "Actividades en hogar",
  deportes: "Deportes intensos",
};

const genderLabels = { M: "Masculino", F: "Femenino" };

const getTagLabel = (item, fallback = "") => {
  if (item === null || item === undefined) return fallback;
  if (typeof item === "string") return item;
  if (typeof item === "number") return item.toString();
  if (typeof item === "object") {
    return (
      item.nombre ||
      item.label ||
      item.texto ||
      item.clave ||
      item._id ||
      fallback
    );
  }
  return fallback;
};

/* ---------- componente ---------- */
export default function DatosPaciente() {
  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [filesPersonales, setFilesPersonales] = useState([]);
  const [filesGenerales, setFilesGenerales] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [errorFiles, setErrorFiles] = useState(null);
  const { user } = useContext(AuthContext);
  const [patient, setPatient] = useState(user?.paciente || null);

  const accentColor = "#2563eb";
  const secondaryAccent = "#0f172a";
  const glassCardStyles = useMemo(
    () => ({
      borderRadius: 4,
      background: "linear-gradient(180deg,#ffffff,#f8fafc)",
      p: { xs: 2.5, md: 3 },
      boxShadow: "0 22px 55px rgba(15,23,42,0.12)",
      border: "1px solid rgba(148,163,184,0.18)",
    }),
    []
  );

  const allFiles = useMemo(
    () => [...filesPersonales, ...filesGenerales],
    [filesPersonales, filesGenerales]
  );

  useEffect(() => {
    setPatient(user?.paciente || null);
  }, [user?.paciente]);

  useEffect(() => {
    setLoading(true);
    HistorialService.fetchHistorial()
      .then((data) => {
        const ordenado = Array.isArray(data)
          ? data.sort((a, b) => new Date(a.creadoEl) - new Date(b.creadoEl))
          : [];
        setHistorial(ordenado);
      })
      .catch((err) => {
        console.error("Error cargando historial:", err);
        setHistorial([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let active = true;

    const fetchLatestPatient = async () => {
      const telefono = user?.telefono;
      const password = user?.password;
      const idNutriologo =
        user?.paciente?.nutriologo || user?.nutriologo || null;

      if (!telefono || !password || !idNutriologo) {
        return;
      }

      try {
        const res = await fetch("/api/paciente/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ telefono, password, idNutriologo }),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || "No se pudo refrescar al paciente");
        }

        const latestPatient = data?.data?.paciente;
        if (active && latestPatient) {
          setPatient(latestPatient);
        }
      } catch (err) {
        console.error("Error refrescando datos del paciente:", err);
      }
    };

    fetchLatestPatient();

    return () => {
      active = false;
    };
  }, [
    user?.telefono,
    user?.password,
    user?.paciente?.nutriologo,
    user?.nutriologo,
  ]);

  useEffect(() => {
    const patientId = patient?._id ? String(patient._id) : null;
    if (!patientId) {
      setLoadingFiles(false);
      return;
    }

    setLoadingFiles(true);
    setErrorFiles(null);
    FilesService.fetchFiles(patientId, user)
      .then(({ generales, personales }) => {
        setFilesGenerales(generales);
        setFilesPersonales(personales);
      })
      .catch((err) => {
        console.error("Error cargando archivos:", err);
        setFilesGenerales([]);
        setFilesPersonales([]);
        setErrorFiles("No se pudieron cargar los archivos.");
      })
      .finally(() => setLoadingFiles(false));
  }, [patient?._id, user]);

  const chartData = useMemo(() => {
    const labels = historial.map((h) => moment(h.creadoEl).format("DD/MM"));
    const pesos = historial.map((h) => parseFloat(h.pesoPaciente) || 0);

    return {
      labels,
      datasets: [
        {
          label: "Peso (kg)",
          data: pesos,
          fill: false,
          tension: 0.2,
          borderColor: accentColor,
          backgroundColor: `${accentColor}33`,
        },
      ],
    };
  }, [historial, accentColor]);

  const hasHistorial = historial.length > 0;

  const fullName = useMemo(() => {
    if (!patient) return "Paciente";
    const name = [patient.nombre, patient.apellidos]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || "Paciente";
  }, [patient]);

  const initials = useMemo(() => {
    if (!patient) return "P";
    const available = [patient.nombre, patient.apellidos]
      .filter(Boolean)
      .join(" ")
      .trim()
      .split(" ");
    if (!available.length) return "P";
    const [first, second] = available;
    const chars = `${first?.[0] || ""}${second?.[0] || ""}`.trim();
    return chars ? chars.toUpperCase() : (first?.[0] || "P").toUpperCase();
  }, [patient]);

  const genderLabel = patient?.sexo
    ? genderLabels[patient.sexo] || patient.sexo
    : "Sin registro";

  const activityLabel = patient?.actividadDiaria
    ? activityLabels[patient.actividadDiaria] || patient.actividadDiaria
    : "Sin registro";

  const performsExercise =
    typeof patient?.preguntaEjercicio === "string" &&
    patient.preguntaEjercicio.toLowerCase() === "si";

  const exerciseAnswer = patient?.preguntaEjercicio
    ? performsExercise
      ? "Sí realiza ejercicio"
      : "No realiza ejercicio"
    : "Sin registro";

  const exerciseFrequency = patient
    ? `${patient.diasPorSemanaEjercicio || 0} días por semana`
    : "Sin registro";

  const dailyExercise = patient
    ? `${patient.horasPorDiaEjercicio || 0} h ${
        patient.minutosPorDiaEjercicio || 0
      } min`
    : "Sin registro";

  const totalExercise = hasValue(patient?.totalTiempoDiaEjercicio)
    ? `${patient.totalTiempoDiaEjercicio} min diarios`
    : "Sin registro";

  const formatSize = (bytes) => {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) return "—";
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    if (value < 1024 * 1024 * 1024)
      return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const getFileIcon = (file) => {
    const type = (file?.contentType || "").toLowerCase();
    const name = (file?.name || "").toLowerCase();

    if (type.includes("pdf") || name.endsWith(".pdf")) {
      return "📄";
    }
    if (
      type.includes("sheet") ||
      type.includes("excel") ||
      name.endsWith(".xls") ||
      name.endsWith(".xlsx")
    ) {
      return "📊";
    }
    if (
      type.includes("word") ||
      type.includes("msword") ||
      name.endsWith(".doc") ||
      name.endsWith(".docx")
    ) {
      return "📝";
    }
    if (
      type.startsWith("image/") ||
      name.endsWith(".jpg") ||
      name.endsWith(".jpeg") ||
      name.endsWith(".png")
    ) {
      return "🖼️";
    }
    return "📎";
  };

  const lastUpdate = formatDate(
    patient?.fechaModificacionDatos,
    "Sin registro"
  );

  const ageInMonths = useMemo(() => deriveAgeInMonths(patient), [patient]);

  const formattedAgeDisplay = useMemo(() => {
    if (ageInMonths === null) return "Sin registro";
    const label = formatAgeFromMonths(ageInMonths);
    return label || "Sin registro";
  }, [ageInMonths]);

  const metricHighlights = useMemo(
    () => [
      {
        label: "Peso actual",
        value: patient
          ? formatMetric(patient.pesoPaciente, "kg", 1)
          : "Sin registro",
      },
      {
        label: "Estatura",
        value: patient
          ? formatMetric(patient.estatura, "cm", 0)
          : "Sin registro",
      },
      {
        label: "IMC",
        value: patient ? formatMetric(patient.imc, "", 2) : "Sin registro",
      },
      {
        label: "FMI",
        value: patient ? formatMetric(patient.FMI, "", 2) : "Sin registro",
      },
      {
        label: "Edad",
        value: formattedAgeDisplay,
      },
    ],
    [patient, formattedAgeDisplay]
  );

  const lifestyleRows = useMemo(() => {
    const rows = [
      { label: "Actividad diaria", value: activityLabel },
      { label: "Declaración de ejercicio", value: exerciseAnswer },
    ];

    if (performsExercise) {
      rows.push(
        { label: "Frecuencia semanal", value: exerciseFrequency },
        { label: "Tiempo por día", value: dailyExercise },
        {
          label: "Tiempo total registrado",
          value: totalExercise,
        },
        {
          label: "Cantidad de ejercicio",
          value: hasValue(patient?.cantidadEjercicio)
            ? patient.cantidadEjercicio
            : "Sin registro",
        }
      );
    }

    return rows;
  }, [
    activityLabel,
    exerciseAnswer,
    performsExercise,
    exerciseFrequency,
    dailyExercise,
    totalExercise,
    patient?.cantidadEjercicio,
  ]);

  const dateRows = [
    {
      label: "Fecha de nacimiento",
      value: formatSimpleDate(patient?.fechaNacimiento, "Sin registro"),
    },
    {
      label: "Registro en plataforma",
      value: formatSimpleDate(patient?.fechaRegistro, "Sin registro"),
    },
    {
      label: "Última cita",
      value: formatSimpleDate(patient?.fechaUltimaCita, "Sin registro"),
    },
    {
      label: "Próxima cita",
      value: patient?.fechaProximaCita
        ? formatSimpleDate(patient.fechaProximaCita)
        : "Por programar",
    },
    {
      label: "Datos actualizados",
      value: lastUpdate,
    },
  ];

  const allergyGroupChips = [...(patient?.gruposAlergias || [])];
  const allergyIngredientChips = [...(patient?.contenidoAlergia || [])];

  const pathologyChips = patient?.patologiasPadecidas || [];
  const pregnancyStatus = hasValue(patient?.embarazoActivo)
    ? patient.embarazoActivo
    : "Sin registro";
  const pregnancyTime = hasValue(patient?.tiempoEmbarazo)
    ? patient.tiempoEmbarazo
    : "Sin registro";
  const showPregnancyInfo =
    patient?.sexo === "F" &&
    typeof patient?.embarazoActivo === "string" &&
    patient.embarazoActivo.toLowerCase() === "si";
  const avatarSrc =
    patient?.imagen && patient.imagen !== "default"
      ? patient.imagen
      : undefined;

  const renderTagGroup = (items = [], emptyLabel) =>
    items.length ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
        {items.map((item, idx) => {
          const label = getTagLabel(item, "Sin dato");
          return (
            <Chip
              key={`${label}-${idx}`}
              label={label}
              size="small"
              sx={{
                borderRadius: 1.5,
                backgroundColor: "rgba(15,23,42,0.07)",
                fontWeight: 600,
              }}
            />
          );
        })}
      </Stack>
    ) : (
      <Typography color="text.secondary">{emptyLabel}</Typography>
    );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 320,
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(135deg,#e0f2fe,#f8fafc)",
          borderRadius: 4,
          boxShadow: "0 18px 45px rgba(15,23,42,0.15)",
          px: 3,
        }}
      >
        <CircularProgress sx={{ color: accentColor }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg,#f1f5f9 0%,#f8fafc 100%)",
        py: { xs: 3, md: 5 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
        <Stack spacing={{ xs: 2.5, md: 3.5 }}>
          {patient ? (
            <>
              <Box
                sx={{
                  ...glassCardStyles,
                  background: "linear-gradient(135deg,#eef2ff,#f8fafc)",
                  border: "1px solid rgba(148,163,184,0.35)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={{ xs: 2.5, md: 4 }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <Avatar
                      src={avatarSrc}
                      sx={{
                        width: 76,
                        height: 76,
                        fontSize: 28,
                        fontWeight: 700,
                        backgroundColor: avatarSrc
                          ? "transparent"
                          : "rgba(15,23,42,0.85)",
                        color: avatarSrc ? "inherit" : "#fff",
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        color={secondaryAccent}
                      >
                        {fullName}
                      </Typography>
                      <Typography color="text.secondary">
                        Paciente desde{" "}
                        {formatSimpleDate(
                          patient?.fechaRegistro,
                          "Sin registro"
                        )}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                        <Chip
                          label={genderLabel}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip
                          label={formattedAgeDisplay}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip
                          label={
                            patient?.telefono
                              ? `Tel. ${patient.telefono}`
                              : "Teléfono no registrado"
                          }
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack
                    spacing={0.5}
                    alignItems={{ xs: "flex-start", md: "flex-end" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Última actualización
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color={secondaryAccent}
                    >
                      {lastUpdate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      FMI actual: {formatMetric(patient?.FMI, "", 2)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 2fr) minmax(0, 1fr)",
                  },
                  gap: { xs: 2.5, md: 3 },
                }}
              >
                <Box sx={glassCardStyles}>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color={secondaryAccent}
                  >
                    Indicadores corporales
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2,minmax(0,1fr))",
                        sm: "repeat(3,minmax(0,1fr))",
                      },
                      gap: { xs: 1.5, sm: 2 },
                      mt: 2,
                    }}
                  >
                    {metricHighlights.map((metric) => (
                      <Box
                        key={metric.label}
                        sx={{
                          borderRadius: 3,
                          border: "1px solid rgba(15,23,42,0.08)",
                          background: "linear-gradient(180deg,#f8fafc,#fff)",
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="caption"
                          textTransform="uppercase"
                          color="text.secondary"
                        >
                          {metric.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={800} mt={0.5}>
                          {metric.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color={secondaryAccent}
                  >
                    Hábitos y ejercicio
                  </Typography>
                  <Stack spacing={1.25} mt={1.5}>
                    {lifestyleRows.map((row, index) => (
                      <Stack
                        key={row.label}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                          py: 0.75,
                          borderBottom:
                            index === lifestyleRows.length - 1
                              ? "none"
                              : "1px solid rgba(15,23,42,0.08)",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {row.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.primary"
                          textAlign="right"
                        >
                          {row.value}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                <Stack spacing={{ xs: 2.5, md: 3 }}>
                  <Box sx={glassCardStyles}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color={secondaryAccent}
                    >
                      Fechas clave
                    </Typography>
                    <Stack spacing={1.25} mt={1.5}>
                      {dateRows.map((row) => (
                        <Stack
                          key={row.label}
                          direction="row"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {row.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            textAlign="right"
                          >
                            {row.value}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  <Box sx={glassCardStyles}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color={secondaryAccent}
                    >
                      Salud y antecedentes
                    </Typography>
                    <Stack spacing={2.5} mt={1.5}>
                      <Box>
                        <Typography
                          variant="caption"
                          textTransform="uppercase"
                          color="text.secondary"
                        >
                          Grupos restringidos
                        </Typography>
                        {renderTagGroup(
                          allergyGroupChips,
                          "Sin grupos registrados"
                        )}
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          textTransform="uppercase"
                          color="text.secondary"
                        >
                          Ingredientes restringidos
                        </Typography>
                        {renderTagGroup(
                          allergyIngredientChips,
                          "Sin ingredientes registrados"
                        )}
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          textTransform="uppercase"
                          color="text.secondary"
                        >
                          Patologías
                        </Typography>
                        {renderTagGroup(
                          pathologyChips,
                          "Sin patologías registradas"
                        )}
                      </Box>
                      {showPregnancyInfo && (
                        <Box>
                          <Typography
                            variant="caption"
                            textTransform="uppercase"
                            color="text.secondary"
                          >
                            Embarazo
                          </Typography>
                          <Typography variant="body2" fontWeight={600} mt={0.5}>
                            {pregnancyStatus}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tiempo: {pregnancyTime}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </>
          ) : (
            <Box sx={glassCardStyles}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Información no disponible
              </Typography>
              <Typography color="text.secondary">
                Inicia sesión nuevamente para sincronizar tus datos personales.
              </Typography>
            </Box>
          )}

          {/* Sección de archivos adjuntos */}
          <Box sx={glassCardStyles}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight={700}
                  color={secondaryAccent}
                >
                  📁 Archivos adjuntos para ti
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Descarga los documentos compartidos por tu nutriólogo.
                </Typography>
              </Box>
            </Stack>

            {loadingFiles ? (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: "center",
                }}
              >
                <CircularProgress size={30} sx={{ color: accentColor }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Cargando archivos…
                </Typography>
              </Box>
            ) : errorFiles ? (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px dashed rgba(239,68,68,0.3)",
                  background: "rgba(254,242,242,0.5)",
                }}
              >
                <Typography color="error.main">{errorFiles}</Typography>
              </Box>
            ) : allFiles.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px dashed rgba(15,23,42,0.15)",
                  background: "linear-gradient(180deg,#f8fafc,#fff)",
                }}
              >
                <Typography fontSize="3rem" sx={{ mb: 1 }}>
                  📂
                </Typography>
                <Typography color="text.secondary">
                  Sin documentos disponibles.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {allFiles.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid rgba(148,163,184,0.2)",
                      background: "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: accentColor,
                        boxShadow: "0 4px 12px rgba(37,99,235,0.1)",
                      },
                    }}
                  >
                    <Typography fontSize="1.5rem">
                      {getFileIcon(file)}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatSize(file.size)} · {file.contentType}
                      </Typography>
                    </Box>
                    <Box
                      component="a"
                      href={file.url}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: `${accentColor}10`,
                        color: accentColor,
                        textDecoration: "none",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: accentColor,
                          color: "#fff",
                        },
                      }}
                      title="Descargar archivo"
                    >
                      <Typography fontSize="1.2rem">⬇️</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={glassCardStyles}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight={700}
                  color={secondaryAccent}
                >
                  Historial de Peso, Estatura e IMC
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revisa tus registros capturados por tu asesor.
                </Typography>
              </Box>
            </Stack>

            {hasHistorial ? (
              <>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ mb: 3, minWidth: 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Peso (kg)</TableCell>
                        <TableCell align="right">Estatura (cm)</TableCell>
                        <TableCell align="right">IMC</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historial.map((row) => (
                        <TableRow key={row._id}>
                          <TableCell>{formatDate(row.creadoEl)}</TableCell>
                          <TableCell align="right">
                            {row.pesoPaciente}
                          </TableCell>
                          <TableCell align="right">{row.estatura}</TableCell>
                          <TableCell align="right">
                            {Number(row.imc).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    height: { xs: 260, sm: 300, md: 360 },
                    px: { xs: 0, sm: 1 },
                  }}
                >
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: { mode: "index", intersect: false },
                      },
                      scales: {
                        y: {
                          title: { display: true, text: "Kg" },
                          ticks: { maxTicksLimit: 6 },
                          grid: { color: "rgba(15,23,42,0.08)" },
                        },
                        x: {
                          title: { display: true, text: "Fecha" },
                          ticks: { maxTicksLimit: 7 },
                          grid: { display: false },
                        },
                      },
                    }}
                  />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  borderRadius: 3,
                  border: "1px dashed rgba(15,23,42,0.15)",
                  background: "linear-gradient(180deg,#f8fafc,#fff)",
                }}
              >
                <Typography color="text.secondary">
                  Sin registros históricos de este paciente.
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
